import {
  Purchases,
  type MakePurchaseResult,
  type PurchasesOfferings,
  type PurchasesPackage,
} from "@revenuecat/purchases-capacitor";
import { Capacitor } from "@capacitor/core";
import { env } from "@config/env";
import { getPlatform } from "@/lib/platform";

export type PurchaseOutcome =
  | { success: true; result: MakePurchaseResult }
  | { success: false; cancelled?: boolean; error?: unknown };

let configurePromise: Promise<void> | null = null;

/**
 * Configure the SDK once. Concurrent callers share the same promise so
 * `logIn`, `getOfferings`, and `purchasePackage` can safely await it.
 */
export function configurePurchases(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return Promise.resolve();
  if (configurePromise) return configurePromise;

  const platform = getPlatform();
  const apiKey =
    platform === "ios" ? env.revenueCatIosKey() : env.revenueCatAndroidKey();

  if (!apiKey) {
    console.warn("[purchases] Missing RevenueCat API key for", platform);
    return Promise.resolve();
  }

  configurePromise = Purchases.configure({ apiKey })
    .then(() => undefined)
    .catch((e) => {
      console.error("[purchases] Failed to configure RevenueCat", e);
      configurePromise = null;
    });

  return configurePromise;
}

export async function logInUser(appUserId: string): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  await configurePurchases();
  try {
    await Purchases.logIn({ appUserID: appUserId });
  } catch (e) {
    console.error("[purchases] logIn failed", e);
  }
}

export async function logOutUser(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  await configurePurchases();
  try {
    await Purchases.logOut();
  } catch (e) {
    console.error("[purchases] logOut failed", e);
  }
}

export async function getOfferings(): Promise<PurchasesOfferings | null> {
  if (!Capacitor.isNativePlatform()) return null;
  await configurePurchases();
  try {
    return await Purchases.getOfferings();
  } catch (e) {
    console.error("[purchases] getOfferings failed", e);
    return null;
  }
}

export async function purchasePackage(
  pkg: PurchasesPackage,
): Promise<PurchaseOutcome> {
  if (!Capacitor.isNativePlatform()) {
    return { success: false, error: new Error("Not a native platform") };
  }
  await configurePurchases();
  try {
    const result = await Purchases.purchasePackage({ aPackage: pkg });
    return { success: true, result };
  } catch (e) {
    if (typeof e === "object" && e !== null && "userCancelled" in e) {
      if ((e as { userCancelled?: boolean }).userCancelled) {
        return { success: false, cancelled: true };
      }
    }
    console.error("[purchases] purchasePackage failed", e);
    return { success: false, error: e };
  }
}
